import { ACTIVITY_GROUP_MODES } from '../constants/activityGroupModes'

// Strips the `cis.user.` prefix to get the bare CIS id used in activity-group URL path segments.
export const getSimpleCisId = (cisId) => cisId?.split?.('cis.user.')?.[1] ?? cisId ?? null

/**
 * Decides whether the URL should be realigned to the signed-in user's own in-progress answer.
 *
 * Context (DSC-9347): when a user clicks "Start Activity" from another person's completed story, the
 * route still points at that person's answer (e.g. `/stories/<ag>/<them>/<theirAnswerId>`). The UI
 * correctly opens a fresh INPUT session for the signed-in user, but the URL disagrees — so a refresh
 * or back-button re-reads the foreign answer id and bumps the user back into the other person's
 * review. Once the user's own answer exists, realign the URL to it so refresh/back/share stay in the
 * user's own session.
 *
 * Returns the realignment URL, or null when no realignment is needed.
 *
 * @returns {string|null}
 */
export function buildOwnAnswerRealignUrl({
  isOpen,
  mode,
  topicId,
  activityGroupId,
  subActivityGroupId,
  ownAnswerId,
  routeAnswerId,
  activeCisId,
}) {
  // Only realign for an open, top-level INPUT session (sub-activities have a different URL shape).
  if (!isOpen) return null
  if (mode !== ACTIVITY_GROUP_MODES.INPUT) return null
  if (subActivityGroupId) return null

  // Need a saved own answer to point at, and a foreign answer in the route to replace.
  if (typeof ownAnswerId !== 'number' || Number.isNaN(ownAnswerId)) return null
  if (typeof routeAnswerId !== 'number' || Number.isNaN(routeAnswerId)) return null

  // The route already points at the user's own answer — nothing to do.
  if (routeAnswerId === ownAnswerId) return null

  const simpleCisId = getSimpleCisId(activeCisId)
  if (!simpleCisId || !topicId || !activityGroupId) return null

  return `/${topicId}/${activityGroupId}/${simpleCisId}/${ownAnswerId}`
}
